import React, { useCallback, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SpeedIcon from '@mui/icons-material/Speed';
import ReplayIcon from '@mui/icons-material/Replay';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import { formatRelativeTime } from './visualizeData.constants';

/**
 * PlaybackControls — transport bar with play/pause, scrubber, speed selector.
 * 
 * Props from usePlayback:
 *   isPlaying, currentTimeSec, durationSec, speed,
 *   play, pause, togglePlay, seekToTime, stepForward, stepBackward, setSpeed, reset, PLAYBACK_SPEEDS
 * Shared from presenter:
 *   playbackTimestamp — pre-formatted absolute timestamp string
 */
const PlaybackControls = ({
  isPlaying,
  currentTimeSec,
  durationSec,
  speed,
  togglePlay,
  seekToTime,
  stepForward,
  stepBackward,
  setSpeed,
  reset,
  PLAYBACK_SPEEDS,
  playbackTimestamp = '',
  hasSignals = false,
}) => {
  const disabled = durationSec <= 0 || !hasSignals;

  // Refs for controlling scrubber
  const rangeRef = useRef(null);
  const wasPlayingRef = useRef(false);
  const isDraggingRef = useRef(false);

  // Sync range input with currentTimeSec
  // Lighter-weight HTML range input allows smooth sync with the rAF animation loop
  useEffect(() => {
    if (rangeRef.current && !isDraggingRef.current) {
      rangeRef.current.value = currentTimeSec;
      const pct = durationSec > 0 ? (currentTimeSec / durationSec) * 100 : 0;
      rangeRef.current.style.setProperty('--fill-pct', `${pct}%`);
    }
  }, [currentTimeSec, durationSec]);

  // Pause rAF loop while dragging.
  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true;
    wasPlayingRef.current = isPlaying; // Store previous playing state
    if (isPlaying) togglePlay();
  }, [isPlaying, togglePlay]);

  const handleDragEnd = useCallback(
    (e) => {
      isDraggingRef.current = false;
      seekToTime(Number(e.target.value));
      if (wasPlayingRef.current) togglePlay();
    },
    [seekToTime, togglePlay],
  );

  const handleRangeChange = useCallback(
    (e) => {
      const val = Number(e.target.value);
      // Update fill track visually
      const pct = durationSec > 0 ? (val / durationSec) * 100 : 0;
      e.target.style.setProperty('--fill-pct', `${pct}%`);
      // Update React state so the time label and all panels follow the drag
      seekToTime(val);
    },
    [durationSec, seekToTime],
  );

  return (
    <Box className="playback-controls-container">
      {/* Transport buttons */}
      <Box className="playback-transport">
        <Tooltip title="Reset">
          <span>
            <IconButton size="small" onClick={reset} disabled={disabled} className="playback-transport-button">
              <ReplayIcon className="playback-transport-icon" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Step backward">
          <span>
            <IconButton size="small" onClick={stepBackward} disabled={disabled || currentTimeSec <= 0} className="playback-transport-button">
              <SkipPreviousIcon className="playback-transport-icon" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={isPlaying ? 'Pause' : 'Play'}>
          <span>
            <IconButton
              onClick={togglePlay}
              disabled={disabled}
              className="playback-play-button"
            >
              {isPlaying ? <PauseIcon className="playback-play-icon" /> : <PlayArrowIcon className="playback-play-icon" />}
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Step forward">
          <span>
            <IconButton size="small" onClick={stepForward} disabled={disabled || currentTimeSec >= durationSec} className="playback-transport-button">
              <SkipNextIcon className="playback-transport-icon" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Scrubber / timeline slider */}
      <Box className="playback-scrubber">
        <Typography variant="caption" className="playback-time" sx={{ minWidth: 56, textAlign: 'right' }}>
          {formatRelativeTime(currentTimeSec)} / {formatRelativeTime(durationSec)}
        </Typography>

        <input
          ref={rangeRef}
          type="range"
          className="playback-range"
          min={0}
          max={durationSec || 1}
          step={durationSec > 0 ? durationSec / 1000 : 0.01}
          defaultValue={currentTimeSec}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          onMouseUp={handleDragEnd}
          onTouchEnd={handleDragEnd}
          onChange={handleRangeChange}
          disabled={disabled}
        />
      </Box>
    
      <Tooltip title="Current data timestamp">
        <Typography
          variant="caption"
          className="playback-timestamp-tooltip"
        >
          {playbackTimestamp}
        </Typography>
      </Tooltip>

      {/* Speed selector */}
      <Box className="playback-speed" sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 0.5,
        ml: 1,
        pl: 1,
        borderLeft: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        <SpeedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Select
          value={speed}
          onChange={(e) => setSpeed(e.target.value)}
          size="small"
          variant="standard"
          disableUnderline
          sx={{
            fontSize: '0.85rem',
            fontWeight: 500,
            color: 'text.primary',
            '& .MuiSelect-select': {
              paddingTop: '2px',
              paddingBottom: '2px',
              paddingLeft: '4px',
              paddingRight: '24px !important',
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              borderRadius: '4px',
            }
          }}
        >
          {PLAYBACK_SPEEDS.map((s) => (
            <MenuItem key={s} value={s} sx={{ fontSize: '0.85rem' }}>
              {s}×
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  );
};

export default PlaybackControls;
